import * as THREE from 'three';
import {
  MODEL_CATALOG,
  createEnvironmentModel,
  type EnvironmentModelId,
  type ModelBiome,
} from '../models/catalog';
import { terrainColor, terrainHeight } from '../world/terrain';
import type { BiomeName } from '../world/biomes';
import type { EnvironmentDefinition } from './environmentCatalog';

export interface PreviewSceneResult {
  readonly scene: THREE.Scene;
  readonly bounds: THREE.Box3;
}

const ENVIRONMENT_MODEL_BIOME: Record<EnvironmentDefinition['id'], ModelBiome> = {
  lanternwood: 'lanternwood',
  blueIce: 'ice-garden',
  timberline: 'timberline',
};

interface EnvironmentModelPlacement {
  readonly id: EnvironmentModelId;
  readonly x: number;
  readonly z: number;
  readonly rotation: number;
  readonly scale: number;
}

/** Authored miniature scenes keep every model legible from all four studies. */
const ENVIRONMENT_COMPOSITIONS: Record<BiomeName, readonly EnvironmentModelPlacement[]> = {
  lanternwood: [
    { id: 'ranger-hut', x: -7, z: -4, rotation: 0.22, scale: 1.5 },
    { id: 'birch-cluster', x: 15, z: -17, rotation: -0.55, scale: 1.28 },
    { id: 'fallen-log', x: 13, z: 7, rotation: 0.2, scale: 1.42 },
    { id: 'snowman', x: -4, z: 9, rotation: -0.3, scale: 1.18 },
    { id: 'snowshoe-hare', x: -15, z: 15, rotation: 1.2, scale: 1.6 },
    { id: 'snow-diving-fox', x: 4, z: 20, rotation: -0.32, scale: 1.18 },
    { id: 'birch-cluster', x: -20, z: -25, rotation: 0.72, scale: 0.9 },
    { id: 'fallen-log', x: 18, z: 19, rotation: -0.54, scale: 0.82 },
  ],
  blueIce: [
    { id: 'ice-arch', x: -7, z: -3, rotation: 0.06, scale: 1.32 },
    { id: 'crystal-fan', x: 15, z: -18, rotation: -0.45, scale: 1.45 },
    { id: 'frozen-icefall', x: 10, z: 3, rotation: -0.18, scale: 1 },
    { id: 'icebound-survey-sled', x: -6, z: 17, rotation: 0.2, scale: 1.05 },
    { id: 'mitten-skidding-ermine', x: -1, z: 17, rotation: -0.5, scale: 1.8 },
    { id: 'crystal-fan', x: -17, z: 12, rotation: 0.52, scale: 1.14 },
    { id: 'ice-arch', x: -20, z: 25, rotation: -0.22, scale: 0.72 },
    { id: 'crystal-fan', x: 18, z: 20, rotation: 0.8, scale: 0.82 },
  ],
  timberline: [
    { id: 'weather-mast', x: -7, z: -3, rotation: 0.12, scale: 1.38 },
    { id: 'avalanche-fence', x: 17, z: -18, rotation: -0.28, scale: 1.18 },
    { id: 'krummholz', x: 9, z: 6, rotation: -0.5, scale: 1.62 },
    { id: 'ribbon-cairn', x: -12, z: 9, rotation: 0.38, scale: 1.52 },
    { id: 'raven-cache', x: -1, z: 19, rotation: -0.36, scale: 1.3 },
    { id: 'powder-bathing-ptarmigan-pair', x: -7, z: 1, rotation: 0.42, scale: 2.3 },
    { id: 'avalanche-fence', x: -20, z: 23, rotation: 0.34, scale: 0.8 },
    { id: 'krummholz', x: 21, z: 24, rotation: 0.52, scale: 1.02 },
    { id: 'ribbon-cairn', x: 2, z: 27, rotation: -0.4, scale: 0.9 },
  ],
};

function addPreviewLighting(scene: THREE.Scene, environment = false): void {
  const hemisphere = new THREE.HemisphereLight(0xf0fbff, environment ? 0x667a79 : 0x7b8582, environment ? 1.7 : 2.1);
  const sun = new THREE.DirectionalLight(0xffe1b8, environment ? 3 : 3.4);
  sun.position.set(-70, 105, 55);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1_024, 1_024);
  sun.shadow.camera.left = -95;
  sun.shadow.camera.right = 95;
  sun.shadow.camera.top = 95;
  sun.shadow.camera.bottom = -95;
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 260;
  sun.shadow.bias = -0.0008;
  scene.add(hemisphere, sun);
}

export function createModelPreviewScene(modelId: string): PreviewSceneResult | undefined {
  const definition = MODEL_CATALOG.find((model) => model.id === modelId);
  if (!definition) return undefined;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xc3dce5);
  scene.fog = new THREE.Fog(0xc3dce5, 45, 95);
  addPreviewLighting(scene);

  const model = definition.create();
  model.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(model);
  const size = new THREE.Vector3();
  bounds.getSize(size);
  const floorRadius = Math.max(size.x, size.z, 2.4) * 0.8;
  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(floorRadius, 32),
    new THREE.MeshStandardMaterial({ color: 0xe8f0ea, roughness: 0.98, flatShading: true }),
  );
  floor.name = 'model-preview-snow-base';
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = bounds.min.y - 0.025;
  floor.receiveShadow = true;
  scene.add(floor, model);
  return { scene, bounds };
}

function createEnvironmentTerrain(environment: EnvironmentDefinition): THREE.Mesh {
  const segmentsX = 22;
  const segmentsZ = 26;
  const width = 150;
  const depth = 172;
  const row = segmentsX + 1;
  const positions = new Float32Array((segmentsX + 1) * (segmentsZ + 1) * 3);
  const colors = new Float32Array(positions.length);
  const indices: number[] = [];
  const anchorHeight = terrainHeight(environment.origin.x, environment.origin.z);

  for (let zIndex = 0; zIndex <= segmentsZ; zIndex += 1) {
    for (let xIndex = 0; xIndex <= segmentsX; xIndex += 1) {
      const index = zIndex * row + xIndex;
      const localX = (xIndex / segmentsX - 0.5) * width;
      const localZ = (zIndex / segmentsZ - 0.5) * depth;
      const worldX = environment.origin.x + localX;
      const worldZ = environment.origin.z + localZ;
      const height = terrainHeight(worldX, worldZ);
      const color = terrainColor(worldX, worldZ, height);
      positions[index * 3] = localX;
      positions[index * 3 + 1] = height - anchorHeight;
      positions[index * 3 + 2] = localZ;
      colors[index * 3] = color[0];
      colors[index * 3 + 1] = color[1];
      colors[index * 3 + 2] = color[2];
    }
  }

  for (let zIndex = 0; zIndex < segmentsZ; zIndex += 1) {
    for (let xIndex = 0; xIndex < segmentsX; xIndex += 1) {
      const topLeft = zIndex * row + xIndex;
      const topRight = topLeft + 1;
      const bottomLeft = topLeft + row;
      const bottomRight = bottomLeft + 1;
      if ((xIndex + zIndex) % 2 === 0) {
        indices.push(topLeft, bottomLeft, bottomRight, topLeft, bottomRight, topRight);
      } else {
        indices.push(topLeft, bottomLeft, topRight, topRight, bottomLeft, bottomRight);
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.96,
      flatShading: true,
    }),
  );
  mesh.name = `environment-terrain-${environment.id}`;
  mesh.receiveShadow = true;
  return mesh;
}

export function createEnvironmentPreviewScene(environment: EnvironmentDefinition): PreviewSceneResult {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(environment.sky);
  scene.fog = new THREE.Fog(environment.sky, 175, 340);
  addPreviewLighting(scene, true);
  scene.add(createEnvironmentTerrain(environment));

  const anchorHeight = terrainHeight(environment.origin.x, environment.origin.z);
  const modelBiome = ENVIRONMENT_MODEL_BIOME[environment.id];
  const allowedIds = new Set(
    MODEL_CATALOG.filter((model) => model.biome === modelBiome).map((model) => model.id),
  );
  for (const placement of ENVIRONMENT_COMPOSITIONS[environment.id]) {
    if (!allowedIds.has(placement.id)) {
      throw new Error(`Model "${placement.id}" does not belong in ${environment.id}.`);
    }
    const model = createEnvironmentModel(placement.id);
    const height = terrainHeight(
      environment.origin.x + placement.x,
      environment.origin.z + placement.z,
    );
    model.position.set(placement.x, height - anchorHeight, placement.z);
    model.rotation.y = placement.rotation;
    model.scale.setScalar(placement.scale);
    scene.add(model);
  }

  const bounds = new THREE.Box3(
    new THREE.Vector3(-54, -30, -64),
    new THREE.Vector3(54, 36, 64),
  );
  return { scene, bounds };
}

export function disposePreviewScene(scene: THREE.Scene): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    geometries.add(object.geometry);
    if (Array.isArray(object.material)) {
      for (const material of object.material) materials.add(material);
    } else {
      materials.add(object.material);
    }
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
}
